import { cn } from "@/lib/utils";
import { Loader2, ArrowUpDown, Crown } from "lucide-react";
import { QuestionSearchResult, SortOption } from "@/hooks/useWordSearch";
import { useAuth } from "@/hooks/useAuth";
import { LanguageFlags } from "./LanguageFlags";
import { AdminDeleteWordButton } from "./AdminDeleteWordButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface QuestionSearchResultsProps {
  results: QuestionSearchResult[];
  isLoading: boolean;
  error: string | null;
  sortOption: SortOption;
  onSortChange: (option: SortOption) => void;
  onResultClick?: (word: string) => void;
  onWordDeleted?: (word: string) => void;
  searchTerm?: string;
}

const FREE_RESULT_LIMIT = 5;

const highlightMatches = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm) return text;
  
  const searchWords = searchTerm.toUpperCase().split(/\s+/).filter(w => w.length > 0);
  if (searchWords.length === 0) return text;
  
  // Create a pattern that matches any of the search words (case insensitive)
  const pattern = new RegExp(`(${searchWords.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  
  const parts = text.split(pattern);
  
  return parts.map((part, index) => {
    const isMatch = searchWords.some(word => part.toUpperCase() === word.toUpperCase());
    if (isMatch) {
      return (
        <strong key={index} className="font-bold text-foreground">
          {part}
        </strong>
      );
    }
    return part;
  });
};

export const QuestionSearchResults = ({ 
  results, 
  isLoading, 
  error, 
  sortOption,
  onSortChange,
  onResultClick,
  onWordDeleted,
  searchTerm = ""
}: QuestionSearchResultsProps) => {
  const { isPremium, isAuthenticated, isAdmin } = useAuth();
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Söker...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8 text-center">
        <p className="text-destructive text-sm">{error}</p>
      </div>
    );
  }

  if (results.length === 0) {
    return null;
  }

  const shouldLimit = !isPremium;
  const displayResults = shouldLimit ? results.slice(0, FREE_RESULT_LIMIT) : results;
  const hiddenCount = shouldLimit ? Math.max(0, results.length - FREE_RESULT_LIMIT) : 0;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-muted-foreground">
          {results.length} resultat hittades
          {shouldLimit && results.length > FREE_RESULT_LIMIT && (
            <span className="text-xs ml-2">(visar {FREE_RESULT_LIMIT})</span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
          <Select value={sortOption} onValueChange={(value) => onSortChange(value as SortOption)}>
            <SelectTrigger className="w-[180px] h-8 text-sm">
              <SelectValue placeholder="Sortera efter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="relevance">Relevans</SelectItem>
              <SelectItem value="alphabetical">Bokstavsordning</SelectItem>
              <SelectItem value="match">Matchningsgrad</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="space-y-2">
        {displayResults.map((result, index) => (
          <div
            key={`${result.word}-${index}`}
            onClick={() => onResultClick?.(result.word)}
            className={cn(
              "px-4 py-3 rounded-lg",
              "bg-secondary/50 text-foreground",
              "border border-border/50",
              "hover:bg-secondary transition-colors",
              onResultClick && "cursor-pointer hover:border-primary/50"
            )}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground">
                    {result.word}
                  </span>
                  <LanguageFlags language={result.language} />
                </div>
                <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
                  {result.clues.map((clue, idx) => (
                    <div key={idx}>
                      • {highlightMatches(clue.charAt(0).toUpperCase() + clue.slice(1).toLowerCase(), searchTerm)}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0 flex items-center gap-2">
                <span className={cn(
                  "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
                  result.matchPercentage >= 80 
                    ? "bg-green-500/20 text-green-700 dark:text-green-400"
                    : result.matchPercentage >= 50
                    ? "bg-yellow-500/20 text-yellow-700 dark:text-yellow-400"
                    : "bg-muted text-muted-foreground"
                )}>
                  {result.matchPercentage}%
                </span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {result.wordLength} bokstäver
                </span>
                {isAdmin && (
                  <AdminDeleteWordButton 
                    word={result.word}
                    onDeleted={() => onWordDeleted?.(result.word)}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Premium upsell for limited results */}
      {shouldLimit && hiddenCount > 0 && (
        <div className="mt-4 flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg text-sm">
          <Crown className="w-4 h-4 text-primary flex-shrink-0" />
          <span className="text-muted-foreground">
            <span className="font-medium text-foreground">+{hiddenCount} fler resultat</span>
            {" – "}
            {isAuthenticated 
              ? "Uppgradera till premium för att se alla resultat."
              : "Logga in och uppgradera för att se alla resultat."
            }
          </span>
        </div>
      )}
    </div>
  );
};
